import "@app/App.css";
import { PeriodEditor } from "@app/grid/PeriodEditor";
import { Button } from "@comps/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@comps/ui/dialog";
import { Label } from "@comps/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { UTCDate } from "@date-fns/utc";
import { Period, PeriodType, PeriodTypeDay, PeriodTypeMonth, PeriodTypeQuarter, PeriodTypeValues, PeriodTypeWeek, PeriodTypeYear, getPeriodTypeLabel } from "@dto/period";
import { GRAY_600_COLOR } from "@utils/colors";
import { viewPeriodStorageId } from "@utils/localStorage";
import { TW_AppDialogButton, TW_ButtonList, TW_PeriodSelectorButton, TW_PeriodSelectorContainer, TW_PeriodSelectorCustomPeriodItemSelectedIcon, TW_PeriodSelectorNavButton, TW_PeriodSelectorNavButtonIcon, TW_PeriodSelectorPeriodButton, TW_PeriodSelectorPeriodList, TW_PeriodSelectorPeriodListItem, TW_PeriodSelectorPeriodListItemSelected, TW_TodayButton, getWsButtonStyle, useHover, useIconHover } from "@utils/twClasses";
import { replacer } from "@utils/utils";
import { ChevronLeftIcon, ChevronRightIcon, Edit2 } from "lucide-react";
import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDataStore, useModelStore } from "src/store";


interface PeriodSelectorProps {
  initPeriod: Period
  columnPeriodType: PeriodType
}

function PeriodSelector({ initPeriod, columnPeriodType }: PeriodSelectorProps) {

  const selectedView = useModelStore((state) => state.selectedView);
  const selectedObjectInfos = useModelStore((state) => state.selectedObjectInfos);
  const updatePeriod = useDataStore((state) => state.updatePeriod);
  const period = useDataStore((state) => state.period);
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCustomPeriodDialogOpen, setIsCustomPeriodDialogOpen] = useState(false);
  const rowActionsEnabled = useDataStore(state => state.rowActionsEnabled);
  const wsColor = useModelStore(state => state.wsColor);
  const hoverCustomPeriod = useIconHover(GRAY_600_COLOR, wsColor);


  const disabled = !rowActionsEnabled;

  const getPeriodSelectionStorageKey = useCallback(() => {
    return viewPeriodStorageId(selectedObjectInfos?.object?._key, selectedView?._key);
  }, [selectedView, selectedObjectInfos]);

  useEffect(() => {
    const periodStr = localStorage.getItem(getPeriodSelectionStorageKey());
    let iPeriod = initPeriod;
    if (periodStr) {
      iPeriod = Period.fromString(periodStr);
    }
    if (iPeriod.toNormalizedString() !== period?.toNormalizedString()) {
      updatePeriod(iPeriod);
    }
  }, [getPeriodSelectionStorageKey, initPeriod]);

  if (!period) {
    return null;
  }

  function updatePeriodStorage(newPeriod: Period | null) {
    localStorage.setItem(getPeriodSelectionStorageKey(), JSON.stringify(newPeriod, replacer));
  }

  const prevPeriod = function (e: MouseEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    if (disabled) {
      return;
    }
    const newPeriod = period?.previous() || null;
    updatePeriod(newPeriod);
    updatePeriodStorage(newPeriod);
  }

  const nextPeriod = function (e: MouseEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    if (disabled) {
      return;
    }
    const newPeriod = period?.next() || null;
    updatePeriod(newPeriod);
    updatePeriodStorage(newPeriod);
  }

  const today = function (e: MouseEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    if (disabled) {
      return;
    }
    const newPeriod = period?.toToday() || null;
    updatePeriod(newPeriod);
    updatePeriodStorage(newPeriod);
  }
  const onPeriodClick = function (e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault()
    e.stopPropagation()
    if (disabled) {
      return;
    }
    setIsDialogOpen(true);
  }

  const onPeriodChange = function (periodType: PeriodType) {
    const newPeriod = period?.toPeriodType(periodType) || null;
    newPeriod.endDate = undefined;
    newPeriod.parsedEndDate = undefined;
    updatePeriod(newPeriod);
    updatePeriodStorage(newPeriod);
    setIsDialogOpen(false);
  }

  const filteredPeriodTypes = PeriodTypeValues.filter((periodType) => periodType >= columnPeriodType);
  filteredPeriodTypes.reverse();


  let buttonWidth = "w-max";

  switch (period.type) {
    case PeriodTypeYear:
      if (period.isCustom()) {
        buttonWidth = "w-20";
      } else {
        buttonWidth = "w-8";
      }
      break;
    case PeriodTypeQuarter:
      if (period.isCustom()) {
        buttonWidth = "w-32";
      } else {
        buttonWidth = "w-14";
      }
      break;
    case PeriodTypeMonth:
      if (period.isCustom()) {
        buttonWidth = "w-56";
      } else {
        buttonWidth = "w-28";
      }
      break;
    case PeriodTypeWeek:
      if (period.isCustom()) {
        buttonWidth = "w-36";
      } else {
        buttonWidth = "w-16";
      }
      break;
    case PeriodTypeDay:
      if (period.isCustom()) {
        buttonWidth = "w-48";
      } else {
        buttonWidth = "w-24";
      }
      break;
    default:
      buttonWidth = "w-max";
  }
  const onCustomPeriodCancel = () => {
    setIsCustomPeriodDialogOpen(false);
  }
  const onCustomPeriodConfirm = (newPeriod: Period) => {
    updatePeriod(newPeriod);
    updatePeriodStorage(newPeriod);
    setIsCustomPeriodDialogOpen(false);
    setIsDialogOpen(false);
  }
  const isCustomPeriod = period.isCustom();

  return (
    <div className={TW_PeriodSelectorContainer}>
      <span className={TW_TodayButton + (disabled ? " opacity-50 cursor-not-allowed" : " opacity-100 cursor-pointer")} onClick={today}>{t("period.today.label")}</span>
      <div className={TW_PeriodSelectorButton}>
        <div className={TW_PeriodSelectorNavButton + (disabled ? " opacity-50 cursor-not-allowed" : " opacity-100 cursor-pointer")} onClick={prevPeriod}><ChevronLeftIcon className={TW_PeriodSelectorNavButtonIcon} /></div>
        <Popover open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <PopoverTrigger className={TW_PeriodSelectorPeriodButton + " " + buttonWidth + " " + (disabled ? " opacity-50 cursor-not-allowed" : " opacity-100 cursor-pointer")} onClick={onPeriodClick} disabled={disabled}>
            {(period ? period.toString(t) : "-")}
          </PopoverTrigger>
          <PopoverContent className={TW_PeriodSelectorPeriodList}>
            {filteredPeriodTypes.map((periodType) => {
              const label = getPeriodTypeLabel(periodType, t);
              const isSelected = periodType === period.type;
              return (
                <div key={periodType}
                  className={(!isCustomPeriod && isSelected) ? TW_PeriodSelectorPeriodListItemSelected : TW_PeriodSelectorPeriodListItem}
                  onClick={() => onPeriodChange(periodType)}>{label}</div>
              )
            })}
            <Dialog open={isCustomPeriodDialogOpen} onOpenChange={setIsCustomPeriodDialogOpen}>
              <DialogTrigger>
                <div className="flex flex-row justify-start items-center gap-2">
                  <div key="custom"
                    className={isCustomPeriod ? TW_PeriodSelectorPeriodListItemSelected : TW_PeriodSelectorPeriodListItem}
                    onClick={() => setIsCustomPeriodDialogOpen(true)}>{t("period.custom.label")}</div>
                  {isCustomPeriod && <Edit2 className={TW_PeriodSelectorCustomPeriodItemSelectedIcon} {...hoverCustomPeriod} />}
                </div>
              </DialogTrigger>
              <DialogContent className="w-104">
                <DialogHeader>
                  <DialogTitle>{t("period.custom.title")}</DialogTitle>
                </DialogHeader>
                <CustomPeriodDialog initPeriod={period} onCancel={onCustomPeriodCancel} onConfirm={onCustomPeriodConfirm} />
              </DialogContent>
            </Dialog>
          </PopoverContent>
        </Popover>
        <div className={TW_PeriodSelectorNavButton + (disabled ? " opacity-50 cursor-not-allowed" : " opacity-100 cursor-pointer")} onClick={nextPeriod}><ChevronRightIcon className={TW_PeriodSelectorNavButtonIcon} /></div>
      </div>
    </div>
  )
}

interface CustomPeriodDialogProps {
  initPeriod: Period;
  onCancel: () => void;
  onConfirm: (newPeriod: Period) => void;
}

function CustomPeriodDialog({ initPeriod, onCancel, onConfirm }: CustomPeriodDialogProps) {

  const { t } = useTranslation();
  const [startDate, setStartDate] = useState(initPeriod.date);
  const [endDate, setEndDate] = useState(initPeriod.endDate);
  const [periodType, setPeriodType] = useState(initPeriod.type);
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const confirmHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

  const onTypeChange = (periodTypeStr: string) => {
    const periodType = parseInt(periodTypeStr) as PeriodType;
    setPeriodType(periodType);
  }

  const onStartPeriodChange = (period: Period | undefined) => {
    setStartDate(period?.date || "");
  }

  const onEndPeriodChange = (period: Period | undefined) => {
    setEndDate(period?.date || "");
  }

  const startPeriod = useMemo(() => {
    return Period.fromDate(new UTCDate(startDate), periodType);
  }, [startDate, periodType]);

  const endPeriod = useMemo(() => {
    if (!endDate || endDate.length === 0) {
      return Period.fromDate(new UTCDate(startDate), periodType);
    }
    return Period.fromDate(new UTCDate(endDate), periodType);
  }, [endDate, periodType, startDate]);

  const onConfirmPeriod = () => {
    const newPeriod = Period.create(startDate, periodType, endDate);
    onConfirm(newPeriod);
  }

  return (
    <div className="flex flex-col justify-start items-start gap-2">
      <div className="flex flex-row justify-end items-center gap-2 w-full">
        <Label className="min-w-1/2 grow text-left">{t("period.custom.type")}</Label>
        <Select value={periodType.toString()} onValueChange={onTypeChange}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder={t("period.custom.type.placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {PeriodTypeValues.map((periodType) => {
              if (periodType === PeriodTypeDay) {
                return null;
              }
              return (
                <SelectItem key={periodType} value={periodType.toString()}>{t("field.period.type." + periodType)}</SelectItem>
              )
            })}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-row justify-end items-center gap-2 w-full">
        <Label className="min-w-1/2 grow text-left">{t("period.custom.start.label")}</Label>
        <PeriodEditor initialPeriod={startPeriod} periodType={periodType} updatePeriod={onStartPeriodChange} disabled={false} className="w-48" />
      </div>
      <div className="flex flex-row justify-end items-center gap-2 w-full">
        <Label className="min-w-1/2 grow text-left">{t("period.custom.end.label")}</Label>
        <PeriodEditor initialPeriod={endPeriod} periodType={periodType} updatePeriod={onEndPeriodChange} disabled={false} className="w-48" />
      </div>
      <div className={TW_ButtonList + " w-full mt-4"}>
        <Button className={TW_AppDialogButton} onClick={onCancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
        <Button className={TW_AppDialogButton} onClick={onConfirmPeriod} {...confirmHover}>{t("general.button.confirm.text")}</Button>
      </div>
    </div>
  )
}

export default PeriodSelector;